import { CLERK_PUBLISHABLE_KEY } from '../config';

// --- 模块作用域变量 ---
let clerk = null;
let userButtonDiv = null;
let customSignInBtn = null;
let customSignUpBtn = null;
let onLoginCallback = () => {}; // 登录回调占位符
let onLogoutCallback = () => {}; // 登出回调占位符

// --- 私有函数 ---

/**
 * 更新认证相关 UI 元素的可见性。
 * @param {boolean} isLoggedIn - 用户是否已登录。
 */
function updateAuthUI(isLoggedIn) {
    // 确保 DOM 元素已获取
    if (!userButtonDiv) userButtonDiv = document.getElementById('user-button');
    if (!customSignInBtn) customSignInBtn = document.getElementById('custom-sign-in-btn');
    if (!customSignUpBtn) customSignUpBtn = document.getElementById('custom-sign-up-btn');

    console.log(`[Auth] Updating UI. Logged in: ${isLoggedIn}`);
    if (userButtonDiv) userButtonDiv.style.display = isLoggedIn ? 'block' : 'none';
    if (customSignInBtn) customSignInBtn.style.display = isLoggedIn ? 'none' : 'block';
    if (customSignUpBtn) customSignUpBtn.style.display = isLoggedIn ? 'none' : 'block';
}

/**
 * 处理用户登录状态。
 * 更新 UI 并调用登录回调。
 * @param {object} session - Clerk session 对象。
 */
async function handleLogin(session) {
    console.log('[Auth] User logged in. Session ID:', session?.id);
    updateAuthUI(true);

    // 挂载 Clerk 用户按钮 (如果需要且未挂载)
    if (userButtonDiv && !userButtonDiv.hasChildNodes() && clerk) {
        try {
            clerk.mountUserButton(userButtonDiv);
            console.log("[Auth] UserButton mounted on login.");
        } catch (mountError) {
            console.error("[Auth] Error mounting UserButton on login:", mountError);
             if (userButtonDiv) userButtonDiv.textContent = '用户菜单加载失败';
        }
    }
    // 调用外部传入的登录回调函数
    await onLoginCallback(session);
}

/**
 * 处理用户登出状态。
 * 更新 UI 并调用登出回调。
 */
function handleLogout() {
    console.log('[Auth] User logged out.');
    updateAuthUI(false);

    // 卸载 Clerk 用户按钮 (如果需要)
    if (userButtonDiv && clerk && typeof clerk.unmountUserButton === 'function') {
         try {
            clerk.unmountUserButton(userButtonDiv);
            console.log("[Auth] UserButton unmounted on logout.");
         } catch(e) {
            // ClerkJS v5 可能没有 unmountUserButton，或者在 v4 中行为不同
            // 清空内容作为备选方案
            console.warn("[Auth] Could not unmount UserButton (might be expected in Clerk v5):", e);
            if (userButtonDiv) userButtonDiv.innerHTML = '';
         }
    }
    // 调用外部传入的登出回调函数
    onLogoutCallback();
}

// --- 公开 API ---

/**
 * 初始化 Clerk 认证服务。
 * 应该在 DOM 加载后，但在需要认证状态的逻辑执行前调用。
 * @param {object} options - 配置选项。
 * @param {function} options.onLogin - 用户登录时调用的回调函数，接收 session 对象。
 * @param {function} options.onLogout - 用户登出时调用的回调函数。
 * @returns {Promise<void>}
 */
export async function initAuth({ onLogin, onLogout }) {
    // 保存回调函数
    onLoginCallback = onLogin || onLoginCallback;
    onLogoutCallback = onLogout || onLogoutCallback;

    console.log('[Auth] Initializing Clerk...');

    // 再次获取 DOM 元素引用，以防 initAuth 被延迟调用
    userButtonDiv = document.getElementById('user-button');
    customSignInBtn = document.getElementById('custom-sign-in-btn');
    customSignUpBtn = document.getElementById('custom-sign-up-btn');

    // 检查 Clerk 全局对象是否存在
    if (typeof Clerk === 'undefined') {
        console.error('[Auth] Clerk object not found! Ensure ClerkJS script is loaded before calling initAuth.');
        updateAuthUI(false); // 确保 UI 处于登出状态
        return;
    }

    // 由于 ClerkJS 脚本是异步加载的，直接赋值 window.Clerk
    clerk = window.Clerk;

    try {
        // 加载 Clerk 实例 (v5 推荐方式)
        await clerk.load({
            // publishableKey: CLERK_PUBLISHABLE_KEY // ClerkJS v5 Script tag has key
        });
        console.log("[Auth] Clerk loaded successfully.");

        // 为自定义按钮绑定事件监听器
        customSignInBtn?.addEventListener('click', () => clerk.openSignIn());
        customSignUpBtn?.addEventListener('click', () => clerk.openSignUp());

        // 添加 Clerk 状态监听器
        clerk.addListener(({ user, session }) => {
            console.log(`[Auth Listener] State changed. User: ${user?.id}, Session: ${session?.id ? 'Active' : 'Inactive'}`);
            if (session) {
                handleLogin(session);
            } else {
                handleLogout();
            }
        });
        console.log("[Auth] Clerk listener added.");

        // 立即执行一次初始状态检查
        console.log('[Auth] Performing initial auth state check...');
        if (clerk.session) {
            handleLogin(clerk.session);
        } else {
            handleLogout();
        }

    } catch (error) {
        console.error("[Auth] Error during Clerk initialization:", error);
        updateAuthUI(false); // 初始化失败时，显示登录/注册按钮
    }
}

/**
 * 检查用户当前是否已登录。
 * @returns {boolean} 如果用户已登录则返回 true，否则返回 false。
 */
export function isUserLoggedIn() {
    // 依赖 Clerk 实例是否已成功初始化且 session 是否存在
    return !!clerk?.session;
}

/**
 * 获取当前的 Clerk session 对象。
 * @returns {object | null} 返回当前的 session 对象，如果未登录则返回 null。
 */
export function getSession() {
    return clerk?.session || null;
}

/**
 * 获取当前的 Clerk user 对象。
 * @returns {object | null} 返回当前的 user 对象，如果未登录则返回 null。
 */
export function getUser() {
     return clerk?.user || null;
}

/**
 * 获取当前的 JWT session token。
 * 此函数供 api.js 调用。
 * @returns {Promise<string | null>} 返回 JWT token，如果未登录或获取失败则返回 null。
 */
export async function getSessionToken() {
    if (!clerk || !clerk.session) {
        console.warn('[Auth] getSessionToken: No active session found.');
        return null;
    }
    try {
        // Clerk v5 推荐使用 getToken()
        const token = await clerk.session.getToken();
        if (!token) {
            console.warn('[Auth] getSessionToken: getToken() returned null or empty.');
            return null;
        }
        return token;
    } catch (error) {
        console.error('[Auth] getSessionToken: Error getting session token:', error);
        // 可能需要处理特定错误，例如 token 过期或网络问题
        return null;
    }
}

/**
 * 主动触发用户登出流程。
 * @returns {Promise<void>}
 */
export async function signOut() {
    if (clerk) {
        try {
            console.log('[Auth] Attempting sign out...');
            await clerk.signOut();
            console.log("[Auth] Sign out successful (listener should handle UI update).");
            // Clerk 的监听器会自动检测到状态变化并调用 handleLogout
        } catch (error) {
            console.error("[Auth] Error signing out:", error);
        }
    } else {
         console.warn("[Auth] signOut called but Clerk is not initialized.");
    }
}
